import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Shield, CheckCircle, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 🛑 CÓDIGOS DE PAYPAL SANDBOX 🛑
const PAYPAL_CLIENT_ID = "AXWiARmhzZl3lLCPa08cqrIvvVmJ8qUFdBuafqpetMDHrvUKluGPkDmZGvAWFjkPtnNbtYTVG071l1CS"; 
const PAYPAL_PLAN_ID = "P-0SU25180MU838894NNG2IAGY"; 

const SubscriptionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  const email = location.state?.email;

  // Verificación segura al montar el componente
  useEffect(() => {
    if (!email && !success) {
      navigate("/admin/login", { replace: true });
    }
  }, [email, success, navigate]);

  const handleApprove = async (subscriptionID) => {
    try {
      await axios.post(`${API}/public/verify-subscription`, {
        email: email,
        subscription_id: subscriptionID
      });
      setSuccess(true);
      toast.success("¡Pago exitoso! Tu cuenta ha sido activada.");
    } catch (error) {
      console.error(error);
      toast.error("Hubo un error al activar tu cuenta en nuestro sistema.");
    }
  };

  // Si no hay email, no renderizamos nada para evitar errores
  if (!email && !success) return null;

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo192.png" alt="Barbershop Logo" className="w-20 h-20 object-contain rounded-xl mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold uppercase text-white mb-2">
            Activa tu Cuenta
          </h1>
          <p className="text-zinc-400">
            Estás a un paso de digitalizar tu barbería
          </p>
        </div>

        {hasError ? (
          <div className="bg-zinc-900 border border-red-500/30 p-8 text-center animate-fade-in shadow-xl rounded-xl">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h2 className="font-heading text-xl font-bold text-white mb-4">
              Error al cargar PayPal
            </h2>
            <p className="text-zinc-400 mb-6 text-sm">
              Por favor, verifica que tu Client ID de PayPal sea correcto y esté bien configurado en el código.
            </p>
            <button onClick={() => window.location.reload()} className="w-full bg-zinc-800 text-white py-3 rounded-lg font-bold hover:bg-zinc-700 transition-colors">
              Reintentar
            </button>
          </div>
        ) : !success ? (
          <div className="bg-zinc-900 border border-zinc-800 p-8 shadow-xl rounded-xl">
            <div className="flex justify-center mb-4">
              <Shield className="w-12 h-12 text-amber-500" />
            </div>
            <h2 className="text-center font-heading text-2xl font-bold uppercase text-white mb-2">
              Suscripción Mensual
            </h2>
            
            {/* 💰 SECCIÓN DE PRECIO Y PRUEBA GRATIS */}
            <div className="my-6 py-5 border-y border-zinc-800/50 text-center">
              <div className="flex items-end justify-center mb-3">
              <span className="text-5xl font-black text-amber-500 tracking-tighter">$10</span>                <span className="text-zinc-400 ml-2 mb-1 font-medium">USD / mes</span>
              </div>
              <div className="inline-flex items-center justify-center bg-green-500/10 border border-green-500/20 px-4 py-2 rounded-full">
                <span className="text-green-400 text-sm font-bold tracking-wide">
                  🎁 7 Días de Prueba Gratis
                </span>
              </div>
            </div>
            {/* ------------------------------------------- */}

            <p className="text-center text-zinc-400 mb-8 text-sm px-2">
              Acceso total al sistema para <strong className="text-amber-500">{email}</strong>. Cancela cuando quieras.
            </p>

            {/* Contenedor blanco para el botón de PayPal para evitar problemas de contraste */}
            <div className="bg-white rounded-lg p-3 min-h-[150px] flex items-center justify-center">
              <PayPalScriptProvider 
                options={{ 
                  "client-id": PAYPAL_CLIENT_ID, 
                  vault: true, 
                  intent: "subscription" 
                }}
              >
                <div className="w-full relative z-0">
                  <PayPalButtons
                    createSubscription={(data, actions) => {
                      return actions.subscription.create({
                        plan_id: PAYPAL_PLAN_ID,
                      });
                    }}
                    onApprove={(data, actions) => {
                      return handleApprove(data.subscriptionID);
                    }}
                    onError={(err) => {
                      console.error("PayPal Error:", err);
                      setHasError(true);
                    }}
                    style={{
                      shape: "rect",
                      color: "gold",
                      layout: "vertical",
                      label: "subscribe"
                    }}
                  />
                </div>
              </PayPalScriptProvider>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-green-500/30 p-8 text-center animate-fade-in shadow-xl rounded-xl">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold uppercase text-white mb-4">
              ¡Suscripción Activa!
            </h2>
            <p className="text-zinc-400 mb-8">
              Tu cuenta ha sido aprobada. Ya puedes iniciar sesión.
            </p>
            <Link to="/admin/login">
              <button className="w-full bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase tracking-wider font-bold h-12 flex items-center justify-center rounded-lg">
                Ir al Panel
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;